import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import axios from "axios";

import { BACKEND_URL as API } from '../utils/apiBase';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const { t } = useI18n();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('forgotPassword.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%)' }}>
      <Card className="w-full max-w-md p-8 bg-[#1a1a2e]/90 border-[#2a2a4a] backdrop-blur-xl" data-testid="forgot-password-card">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">{t('forgotPassword.title')}</h1>
          <p className="text-gray-400">{t('forgotPassword.subtitle')}</p>
        </div>

        {sent ? (
          <div className="text-center space-y-4" data-testid="forgot-success">
            <CheckCircle size={48} className="mx-auto text-green-400" />
            <p className="text-green-400 font-medium text-lg">{t('forgotPassword.sent')}</p>
            <p className="text-gray-400 text-sm">{t('forgotPassword.checkInbox')}</p>
            <Button onClick={() => navigate("/login")} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800" data-testid="back-to-login-btn">
              <ArrowLeft size={16} className="mr-2" /> {t('forgotPassword.backToLogin')}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="forgot-error">
                <AlertCircle size={16} /> {error}
              </div>
            )}
            <div>
              <Label className="text-gray-300 mb-2 block"><Mail size={13} className="inline mr-1" />{t('login.email')}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                className="bg-[#0f0f1a] border-[#2a2a4a] text-white h-12"
                data-testid="forgot-email-input"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white font-semibold"
              data-testid="forgot-submit-btn"
            >
              {loading ? t('forgotPassword.sending') : t('forgotPassword.submit')}
            </Button>
            <div className="text-center">
              <button type="button" onClick={() => navigate("/login")} className="text-purple-400 hover:text-purple-300 text-sm underline" data-testid="back-to-login-link">
                <ArrowLeft size={14} className="inline mr-1" /> {t('forgotPassword.backToLogin')}
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
